import axios from 'axios';
import toast from 'react-hot-toast';

// API base configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add JWT token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      localStorage.removeItem('token');
      window.location.href = '/#/login';
      toast.error('เซสชั่นหมดอายุ กรุณาเข้าสู่ระบบใหม่');
    } else if (error.response?.status === 403) {
      toast.error('ไม่มีสิทธิ์เข้าถึง');
    } else if (error.code === 'ECONNABORTED') {
      toast.error('การเชื่อมต่อล่าช้า กรุณาลองใหม่');
    } else if (!error.response) {
      toast.error('ข้อผิดพลาดเครือข่าย กรุณาตรวจสอบการเชื่อมต่อ');
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: async (credentials) => api.post('/auth/login', credentials),
  loginWithLine: async (lineProfile) => api.post('/auth/line-login', lineProfile),
  validateToken: async () => api.get('/auth/validate'),
};

export const userAPI = {
  getUsers: async (params) => api.get('/users', { params }),
  getUserLimits: async () => api.get('/users/limits'),
  approveUser: async (id) => api.post(`/users/${id}/approve`),
  activateUser: async (id) => api.post(`/users/${id}/activate`),
  deactivateUser: async (id) => api.post(`/users/${id}/deactivate`),
  updateUser: async (id, data) => api.patch(`/users/${id}`, data),
};

export const agentAPI = {
  getAgents: async () => api.get('/agents'),
  getDownlines: async () => api.get('/agents/downlines'),
  getMyProfile: async () => api.get('/agents/me'),
  getAgent: async (id) => api.get(`/agents/${id}`),
  createAgent: async (data) => api.post('/agents', data),
  updateAgent: async (id, data) => api.patch(`/agents/${id}`, data),
  deleteAgent: async (id) => api.delete(`/agents/${id}`),
  importBulk: async (data) => api.post('/agents/import-bulk', data, { timeout: 30000 }),
};

export const trainingAPI = {
  getTrainingsByAgent: async (agentId) => api.get(`/trainings/agent/${agentId}`),
  getTraining: async (id) => api.get(`/trainings/${id}`),
  createTraining: async (data) => api.post('/trainings', data),
  updateTraining: async (id, data) => api.patch(`/trainings/${id}`, data),
  deleteTraining: async (id) => api.delete(`/trainings/${id}`),
};

export const policyAPI = {
  getPoliciesByAgent: async (agentId) => api.get(`/policies/agent/${agentId}`),
  getPolicy: async (id) => api.get(`/policies/${id}`),
  createPolicy: async (data) => api.post('/policies', data),
  updatePolicy: async (id, data) => api.patch(`/policies/${id}`, data),
  deletePolicy: async (id) => api.delete(`/policies/${id}`),
};

export const coachingAPI = {
  getCoachingLogsByAgent: async (agentId) => api.get(`/coaching-logs/agent/${agentId}`),
  getCoachingLog: async (id) => api.get(`/coaching-logs/${id}`),
  createCoachingLog: async (data) => api.post('/coaching-logs', data),
  updateCoachingLog: async (id, data) => api.patch(`/coaching-logs/${id}`, data),
  deleteCoachingLog: async (id) => api.delete(`/coaching-logs/${id}`),
};

export const calendarAPI = {
  getEvents: async () => api.get('/calendar-events'),
  getSystemEvents: async (month, year) => api.get('/calendar-events/system-events', { params: { month, year } }),
  createEvent: async (data) => api.post('/calendar-events', data),
  updateEvent: async (id, data) => api.patch(`/calendar-events/${id}`, data),
  deleteEvent: async (id) => api.delete(`/calendar-events/${id}`)
};

export const performanceAPI = {
  getPerformanceByAgent: async (agentId) => api.get(`/performance/${agentId}`),
  getAdminManagerPerformance: async () => api.get('/performance/admin/managers'),
};

export const dashboardAPI = {
  getLineDashboardStats: async (agentId) => api.get(`/dashboard/line/${agentId}`),
  getAdminDashboardStats: async () => api.get('/dashboard/admin'),
  getLineDashboardEvents: async (agentId, month) => api.get(`/dashboard/line/${agentId}/events`, { params: { month } }),
  getAdminDashboardEvents: async (month) => api.get('/dashboard/admin/events', { params: { month } }),
  getAdminAlerts: async () => api.get('/dashboard/admin/alerts'),
};

export const ticketAPI = {
  getTickets: () => api.get('/tickets'),
  getTicket: (id) => api.get(`/tickets/${id}`),
  createTicket: (data) => api.post('/tickets', data),
  updateTicket: (id, data) => api.patch(`/tickets/${id}`, data),
  notifyAgain: (id) => api.post(`/tickets/${id}/notify`),
  forceClose: (id) => api.post(`/tickets/${id}/force-close`),
  extractFromImage: (data) => api.post('/tickets/extract-ai', data, { timeout: 60000 }),
};

export const notificationAPI = {
  getNotifications: () => api.get('/notifications'),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (id) => api.put(`/notifications/${id}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (id) => api.delete(`/notifications/${id}`),
  getSettings: () => api.get('/notifications/settings'),
  updateSettings: (data) => api.put('/notifications/settings', data),
  sendTestNotification: (data) => api.post('/notifications/test-send', data),
};

export default api;
